import React, { useEffect, useRef, useState } from 'react';
import { FormikValues } from 'formik';
import styled from 'styled-components';

import { Card, FormHelperText, Grid, IconButton } from '@material-ui/core';
import {
  Assignment as AssignmentIcon,
  Info as InfoIcon
} from '@material-ui/icons';
import { InlineForm, InlineFormik } from '@sierralabs/react-material-ui-formik';

import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import CardTitleContent from '../../../common/card/CardTitleContent';
import InlineSelect from '../../../common/inline/InlineSelect';
import InlineTextField from '../../../common/inline/InlineTextField';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { getRiskLabel } from '../Hazard.helpers';
import { useUpdateHazardCause } from '../Hazard.hooks';
import {
  CauseProbabilityTable,
  HazardCause,
  HazardProbability,
  hazardProbability
} from '../Hazard.interface';
import { HazardCardInfoDialog } from '../dialogs/HazardCardInfoDialog';

const StyledCard = styled(Card)`
  .form-control {
    width: 150px;
  }
`;

const HazardCauseInitialAssessmentCard: React.FC<{
  hazardId: number;
  cause: HazardCause;
  onChange?: (cause: HazardCause) => void;
}> = ({ hazardId, cause, onChange }) => {
  const { initialHarm, hazard } = cause;
  const [showInfo, setShowInfo] = useState(false);
  const {
    updatedHazardCause,
    isUpdatingHazardCause,
    updateHazardCauseError,
    updateHazardCause
  } = useUpdateHazardCause(hazardId, cause.id);
  const formikRef = useRef<FormikValues>();
  const onChangeRef = useRef(onChange);
  const { isFieldDisabled } = useTaskEntityFields(cause.task);
  useEffect(() => {
    if (updatedHazardCause) {
      onChangeRef.current?.(updatedHazardCause);
    }
  }, [updatedHazardCause]);
  return (
    <StyledCard>
      <CardTitleContent>
        <CardTitle>
          <AssignmentIcon />
          Initial Likelihood of Harm and Risk
        </CardTitle>
        <IconButton size='small' onClick={() => setShowInfo(true)}>
          <InfoIcon />
        </IconButton>
      </CardTitleContent>
      <CardContent padding='large'>
        <InlineFormik
          error={updateHazardCauseError}
          isSubmitting={isUpdatingHazardCause}
          innerRef={(formik: any) => (formikRef.current = formik)}
          initialValues={{
            initialHarm: initialHarm || ('' as any as HazardProbability),
            initialRiskLabel: getRiskLabel(hazard.initialRpn)
          }}
          onSubmit={(dto: Partial<HazardCause>) => updateHazardCause(dto)}
        >
          <InlineForm>
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={6}>
                <FormHelperText>Initial likelihood of harm</FormHelperText>
                <InlineSelect
                  name='initialHarm'
                  label=''
                  disabled={isFieldDisabled('initialHarm')}
                  options={hazardProbability.map(s => ({
                    value: s.value,
                    label: `${s.value} - ${s.label}`
                  }))}
                />
              </Grid>
              <Grid item xs={6}>
                <FormHelperText>Initial risk</FormHelperText>
                <InlineTextField name='initialRiskLabel' disabled />
              </Grid>
            </Grid>
          </InlineForm>
        </InlineFormik>
      </CardContent>
      {showInfo && (
        <HazardCardInfoDialog
          title='Cause Probability Table'
          table={CauseProbabilityTable}
          onClose={() => setShowInfo(false)}
        />
      )}
    </StyledCard>
  );
};

export default HazardCauseInitialAssessmentCard;
